import {
  ARTIST_STATUS_DROPDOWN_ITEMS,
  COUNTRY_SELECT_OPTIONS,
  EMAIL_TEMPLATES,
  PUZZLE_STATUSES,
  PUZZLE_DROPDOWN_OPTIONS as STATUS_DROPDOWN_OPTIONS,
} from 'app-constants';
import { User } from 'assets/LoginPage';
import { useModalProvider } from 'context/ModalProviderContext';
import { useRemoteConfig } from 'context/RemoteConfigContextProvider';
import { deleteField, doc, DocumentSnapshot, runTransaction } from 'firebase/firestore';
import { getCombinedState } from 'hooks';
import useForm from 'hooks/useForm';
import { useSetting } from 'hooks/useSettings';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useFieldArray } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';
import { artisteRejectedNotificationApi, firestore, STORAGE_URL } from 'services';
import { sendEmail } from 'services/api/sendEmail';
import { Button, Input, RejectionModal, SelectForm, Splash } from 'shared/Components';
import { ColoringGridImage } from 'shared/Components/ColoringGridImage';
import { InformationModal, InformationModalTypes } from 'shared/Components/InformationModal';
import { SelectMultiForm } from 'shared/Components/Select/SelectMultiForm';
import { Status } from 'shared/enums';
import { Box, FlexContainer, Image, Text } from 'shared/Typhography';
import { FlexRow } from 'shared/Typhography/flex-box';
import { Container } from 'shared/Typhography/GridSystem';
import { LinkButton } from 'shared/Typhography/LinkButton';
import { strings } from 'strings';
import { theme } from 'theme';
import { Artist } from 'types';
import { buildLookupObject, isSuccessStatusCode } from 'utilities';
import { buildPreviousVersionUrl } from 'utilities/puzzleData';
import { EditPuzzleField, UpdateField } from 'validations';

import { useFirstApprovalData } from './hooks/useFirstApprovalData';
import {
  FirstApprovalFormFields,
  getFirstApprovalFormSchema,
  GridFormFields,
  PuzzleFormFields,
} from './validations/firstApprovalFormValidation';

export type Option = {
  label: string;
  value: string;
};

export const FirstApprovalScreen = () => {
  const {
    artistSnapshot,
    puzzlesSnapshot,
    gridsSnapshot,
    categoriesSnapshot,
    gridsCategoriesSnapshot,
    state: dataFetchState,
    errors: dataFetchErrors,
  } = useFirstApprovalData();

  const { urls, state: settingsFetchState, error: settingsFetchError } = useSetting('urls');
  const { host } = useRemoteConfig();
  const queryClient = useQueryClient();
  const { openModal, closeModal } = useModalProvider();
  const [onSubmitErrors, setOnSubmitErrors] = useState<any[]>([]);
  const history = useHistory();

  // Prepare for easy mapping of ids to values.
  const categoriesMap = useMemo(
    () => (dataFetchState === 'success' ? buildLookupObject(categoriesSnapshot, 'id') : {}),
    [dataFetchState, categoriesSnapshot],
  );

  const puzzlesMap = useMemo(
    () => (dataFetchState === 'success' ? buildLookupObject(puzzlesSnapshot, 'id') : {}),
    [dataFetchState, puzzlesSnapshot],
  );

  const gridsMap = useMemo(
    () => (dataFetchState === 'success' ? buildLookupObject(gridsSnapshot, 'id') : {}),
    [dataFetchState, gridsSnapshot],
  );

  const firstApprovalSchema = useMemo(
    () =>
      getFirstApprovalFormSchema(artistSnapshot?.id as string, artistSnapshot?.data() as Artist),
    [artistSnapshot],
  );

  const categorySelectOptions = categoriesSnapshot.map((categorySnapshot) => ({
    value: categorySnapshot.id,
    label: categorySnapshot.data()?.name,
  }));

  const gridCategorySelectOptions = useMemo(() => {
    return gridsCategoriesSnapshot.map((categorySnapshot) => ({
      value: categorySnapshot.id,
      label: categorySnapshot.data()?.name,
    }));
  }, [gridsCategoriesSnapshot]);

  const {
    handleSubmit,
    getValues,
    setValue,
    trigger: triggerFormValidation,
    control,
    formState: { errors: formErrors },
  } = useForm<FirstApprovalFormFields>({
    schema: firstApprovalSchema,
  });

  const artistValues = getValues('artist');

  const {
    fields: puzzleFields,
    append: appendPuzzleFields,
    update: updatePuzzleFields,
  } = useFieldArray({
    name: 'puzzles',
    control,
  });

  const {
    fields: gridFields,
    append: appendGridFields,
    update: updateGridFields,
  } = useFieldArray({
    name: 'coloringGrids',
    control,
  });

  /**
   * Opens rejection modal that allows giving more info on rejection reason.
   *
   * @function openRejectionModal
   * @param type Type of rejection modal.
   * @param puzzleIndex Index of puzzle gotten from form's fields.map.
   */
  const openRejectionModal = useCallback(
    (type: 'puzzle' | 'coloringGrid' | 'artist', puzzleIndex?: number, id?: string) => {
      openModal(`reject-${type}-${id}-modal-message`, RejectionModal as React.FC, {
        rejectionModalType: type,
        onClose: () => closeModal(`reject-${type}-${id}-modal-message`),
        onSubmit: async (message: string) => {
          if (type === 'puzzle' && typeof puzzleIndex === 'number') {
            updatePuzzleFields(puzzleIndex, {
              ...getValues(`puzzles.${puzzleIndex}`),
              rejectMessage: message,
            });

            await triggerFormValidation(`puzzles.${puzzleIndex}.status`);
          } else if (type === 'coloringGrid' && typeof puzzleIndex === 'number') {
            updateGridFields(puzzleIndex, {
              ...getValues(`coloringGrids.${puzzleIndex}`),
              rejectMessage: message,
            });
            await triggerFormValidation(`coloringGrids.${puzzleIndex}.status`);
          } else if (type === 'artist') {
            // @ts-expect-error There is some typescript inference issue that expects value "never" here which isn't correct. Same as lines below.
            setValue('artist.rejectMessage', message);

            // When rejecting artist, also move all his puzzles to rejected.
            // Adding reason for each puzzle is not necessary because artist will receive "creator-rejected" template
            // which doesn't contain puzzle related data.
            puzzleFields.forEach((_field, index) => {
              setValue(
                `puzzles.${index}.status`,
                // @ts-expect-error There is some typescript inference issue that expects value "never" here which isn't correct.
                STATUS_DROPDOWN_OPTIONS.find((status) => status.value === 'rejected') as Option,
              );
            });

            gridFields.forEach((_field, index) => {
              setValue(
                `coloringGrids.${index}.status`,
                STATUS_DROPDOWN_OPTIONS.find((status) => status.value === 'rejected') as Option,
              );
            });

            await triggerFormValidation(`artist.status`);
          } else {
            toast.error("Rejection reason didn't save properly");
          }
        },
        id: id,
      });
    },
    [openModal, closeModal, setValue, puzzleFields, triggerFormValidation],
  );

  // Add state to track submission process...
  const [isSubmittingData, setIsSubmittingData] = useState(false);

  /**
   * Submits form data and notifies artist of results by email.
   *
   * @function submit
   * @see sendEmail
   */
  const submit = handleSubmit(async (data) => {
    try {
      if (!artistSnapshot || !host) return;

      setOnSubmitErrors([]);
      setIsSubmittingData(true);

      await runTransaction(firestore, async (transaction) => {
        // Update artist data
        transaction.update(artistSnapshot.ref, {
          Country: data.artist.country.label,
          Description: data.artist.description,
          Name: data.artist.name?.trim(),
          username: data.artist.DisplayName?.trim(),
          portfolioUrl: data.artist.portfolioUrl,
          profileLink: data.artist.profileLink,
          revenueShare: data.artist.revenueShare ? data.artist.revenueShare / 100 : null,
          status: data.artist.status.value,
          // TODO #8698kcrpf: This comment needs to be updated after I get all information from PM.
          // This is a flag field that is only needed to during first approval for routing.
          // Business logic dictates that if artist is left pending he still needs to pass initial approval.
          // Once that is done - artist is either rejected or approved - this flag is not needed anymore and is deleted.
          requiresInitialApproval:
            (data.artist.status.value as string) !== Status.Approved ? true : deleteField(),
          ...(data.artist.rejectMessage ? { rejectMessage: data.artist.rejectMessage } : {}),
        });

        // Update puzzles' data
        for (const puzzle of data.puzzles ?? []) {
          const collections = [puzzle[EditPuzzleField.Collection].value];

          if (puzzle[EditPuzzleField.SecondaryCollection]) {
            collections.push((puzzle[EditPuzzleField.SecondaryCollection] as Option).value);
          }

          transaction.update(doc(firestore, `puzzles/${puzzle.puzzleId}`), {
            name: puzzle[EditPuzzleField.Name],
            keywords: puzzle[EditPuzzleField.Keywords],
            collections: collections,
            status: puzzle[EditPuzzleField.Status].value,
            physicalDistribution: Boolean(puzzle[EditPuzzleField.PhysicalDistribution]),
            aiGenerated: Boolean(puzzle[EditPuzzleField.AIGenerated]),
            premiumTier: puzzle[EditPuzzleField.PremiumTier]?.value ?? 0,
            ...(puzzle[EditPuzzleField.RejectMessage]
              ? { rejectMessage: puzzle[EditPuzzleField.RejectMessage] }
              : {}),
            ...(puzzle[EditPuzzleField.Status].value === 'rejected'
              ? { '_meta.previousCollection': puzzle[EditPuzzleField.Collection].value }
              : {}),
          });
        }

        // Update coloring grids' data
        for (const grid of data.coloringGrids ?? []) {
          const collections = grid.collections.map((collection) => collection.value as string);

          transaction.update(doc(firestore, `coloringGrids/${grid.gridId}`), {
            name: grid.name,
            keywords: grid.keywords.split(' '),
            collections: collections,
            status: grid.status.value,
            aiGenerated: Boolean(grid.aiGenerated),
            premiumTier: grid.premiumTier,
            ...(grid.rejectMessage && { rejectMessage: grid.rejectMessage }),
          });
        }

        /*
         * Send email depending on artist status:
         * 1. "approved" -> "EMAIL_TEMPLATES.creatorApprovedWithSubmissions": contains artist and all puzzles info (approved and rejected)
         * 2. "rejected" -> "creator-rejected": contains only artist rejection with reject reason.
         * 3. "pending"  -> "EMAIL_TEMPLATES.submissionMultiple": contains only puzzles info, this is used to avoid rejecting promising
         * artists whose puzzles still need fixes.
         */
        const puzzleSubmissionData =
          data.puzzles?.map((puzzle) => {
            const url = new URL(`${urls?.jpuAppBaseUrl}/puzzle/${puzzle.puzzleId}`);

            return {
              name: puzzle[EditPuzzleField.Name],
              isApproved: puzzle[EditPuzzleField.Status].value === 'approved',
              iconURL: puzzlesMap[puzzle.puzzleId]?.data()?.iconURL,
              rejectReason: puzzle[EditPuzzleField.RejectMessage] ?? '',
              linkHref:
                puzzle[EditPuzzleField.Status].value === 'approved' ? url.toString() : undefined,
              linkText:
                puzzle[EditPuzzleField.Status].value === 'approved'
                  ? url.host + url.pathname
                  : undefined,
            };
          }) ?? [];

        const gridsSubmissionData =
          data.coloringGrids?.map((coloringGrid) => {
            const url = new URL(`${urls?.jpuAppBaseUrl}/coloring-grids/${coloringGrid.gridId}`);

            return {
              name: coloringGrid.name,
              isApproved: coloringGrid.status.value === 'approved',
              iconURL: gridsMap[coloringGrid.gridId]?.data()?.iconUrl,
              rejectReason: coloringGrid.rejectMessage ?? '',
              linkHref: coloringGrid.status.value === 'approved' ? url.toString() : undefined,
              linkText:
                coloringGrid.status.value === 'approved' ? url.host + url.pathname : undefined,
            };
          }) ?? [];

        if (data.artist.status.value === 'approved') {
          await sendEmail(EMAIL_TEMPLATES.creatorApprovedWithSubmissions, {
            email: artistSnapshot.data()?.email,
            name: data.artist.name,
            getStartedUrl: host,
            storageUrl: STORAGE_URL,
            submissions: [...puzzleSubmissionData, ...gridsSubmissionData],
          });
        } else if (data.artist.status.value === 'rejected') {
          const response = await artisteRejectedNotificationApi(
            artistSnapshot.id,
            data.artist.rejectMessage,
          );

          if (!isSuccessStatusCode(response.data.status)) {
            throw new Error(response.data.message);
          }
        } else if (data.artist.status.value === 'pending') {
          await sendEmail(EMAIL_TEMPLATES.submissionMultiple, {
            email: artistSnapshot.data()?.email,
            storageUrl: STORAGE_URL,
            submissions: [...puzzleSubmissionData, ...gridsSubmissionData],
          });
        }

        setOnSubmitErrors([]);
        toast.success('Submission successful!');
        await queryClient.resetQueries(['artists']);
      });

      history.push(`/view-artist/${artistSnapshot.id}`);
    } catch (error) {
      toast.error('Error setting data');
      setOnSubmitErrors((previousErrors) => [...(previousErrors ?? []), error]);
    } finally {
      setIsSubmittingData(false);
    }
  });

  const artistStatus = ARTIST_STATUS_DROPDOWN_ITEMS.find(
    (status) => status.value === artistSnapshot?.data()?.[UpdateField.Status],
  );
  const artistCountry = COUNTRY_SELECT_OPTIONS.find(
    (country) => country.label === artistSnapshot?.data()?.Country,
  );

  useEffect(
    function dynamicallyCreateFormFields() {
      // Only append once after data is loaded.
      if (!puzzleFields.length && dataFetchState === 'success') {
        puzzlesSnapshot.forEach((puzzleSnapshot) => {
          const puzzleDataToFormFieldMap: PuzzleFormFields = {
            puzzleId: puzzleSnapshot.id, // Puzzle id is only for establishing mapping with other puzzle data during `fields.map` operation.
            [EditPuzzleField.Name]: puzzleSnapshot.data()?.name,
            [EditPuzzleField.Keywords]: puzzleSnapshot.data()?.keywords,
            [EditPuzzleField.Publisher]: {
              value: (artistSnapshot as DocumentSnapshot).id,
              label: (artistSnapshot as DocumentSnapshot).data()?.Name,
            },
            [EditPuzzleField.Collection]: {
              value: puzzleSnapshot.data()?.collections?.[0] as string,
              label: categoriesMap[puzzleSnapshot.data()?.collections?.[0] as string]?.data()?.name,
            },
            [EditPuzzleField.SecondaryCollection]: null,
            [EditPuzzleField.AIGenerated]: puzzleSnapshot.data()?.aiGenerated,
            [EditPuzzleField.PhysicalDistribution]: false,
            [EditPuzzleField.Status]:
              STATUS_DROPDOWN_OPTIONS.find(
                (option) => option.value === puzzleSnapshot?.data()?.status,
              ) ?? STATUS_DROPDOWN_OPTIONS[0],
            [EditPuzzleField.RejectMessage]: puzzleSnapshot?.data()?.rejectMessage || '',
            [EditPuzzleField.PremiumTier]: null,
          };

          appendPuzzleFields(puzzleDataToFormFieldMap);
        });
      }

      if (!gridFields.length && dataFetchState === 'success') {
        gridsSnapshot.forEach((gridSnapshot) => {
          const gridDataToFormFieldMap: GridFormFields = {
            gridId: gridSnapshot.id, // Grid id is only for establishing mapping with other grid data during `fields.map` operation.
            name: gridSnapshot.data()?.name,
            keywords: gridSnapshot.data()?.keywords.join(' '),
            publisher: {
              value: (artistSnapshot as DocumentSnapshot).id,
              label: (artistSnapshot as DocumentSnapshot).data()?.Name,
            },
            collections: gridSnapshot.data().collections.map((collection) => {
              return {
                value: collection,
                label: gridCategorySelectOptions.find((option) => option.value === collection)
                  ?.label,
              };
            }),
            aiGenerated: gridSnapshot.data()?.aiGenerated,
            status:
              STATUS_DROPDOWN_OPTIONS.find(
                (option) => option.value === gridSnapshot?.data()?.status,
              ) ?? STATUS_DROPDOWN_OPTIONS[0],
            rejectMessage: gridSnapshot.data()?.rejectMessage ?? '',
            premiumTier: gridSnapshot.data()?.premiumTier,
          };

          appendGridFields(gridDataToFormFieldMap);
        });
      }
    },
    [dataFetchState, puzzlesSnapshot, artistSnapshot],
  );

  const gapBetweenFields = '24px';
  const combinedFetchState = getCombinedState(dataFetchState, settingsFetchState);
  const combinedErrors = [
    ...(dataFetchErrors ?? []),
    ...(settingsFetchError ?? []),
    ...(onSubmitErrors ?? []),
  ].filter((error) => Boolean(error));

  return (
    <Container>
      {combinedFetchState === 'loading' && (
        <Box>
          <Splash color={theme.colors.purple} />
        </Box>
      )}

      {dataFetchState === 'success' && (
        <form onSubmit={submit}>
          <h2>Puzzles</h2>
          {puzzleFields.length === 0 && <Text>No puzzles to review.</Text>}
          {puzzleFields.map((field, index) => {
            if (!field.puzzleId) return <></>;
            const puzzleDocumentData = puzzlesMap[field.puzzleId]?.data();

            const previewImageUrlLink =
              puzzleDocumentData?.type === 'video'
                ? `${STORAGE_URL}/${puzzleDocumentData?._meta?.original}`
                : puzzleDocumentData?.url;

            const previousVersionUrl = buildPreviousVersionUrl({
              type: puzzleDocumentData.type,
              url: puzzleDocumentData.url,
              uuid: field.puzzleId,
            });

            return (
              <Box key={field.id} width="100%" margin="32px 0" padding="32px">
                <FlexContainer gap="2rem">
                  <div>
                    <a href={previewImageUrlLink} target="_blank" rel="noreferrer">
                      <Image
                        width="256px"
                        height="256px"
                        cursor="pointer"
                        src={puzzleDocumentData?.iconURL}
                        borderRadiusType="rounded"
                        alt="Picture"
                      />
                    </a>
                    <FlexContainer justifyContent="center" margin="10px 0 0 0">
                      {puzzleDocumentData?.status === Status.Edited && (
                        <LinkButton
                          href={previousVersionUrl}
                          target="_blank"
                          rel="noopener noreferer"
                          style={{ marginTop: '8px' }}
                        >
                          Previous Version
                        </LinkButton>
                      )}
                    </FlexContainer>
                  </div>

                  <FlexContainer flexDirection="column" gap="2.5rem" justifyContent="space-between">
                    <FlexContainer
                      gap={gapBetweenFields}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Input
                        required
                        id={`puzzles.${index}.name`}
                        label="Name"
                        placeholder="Name"
                        name={`puzzles.${index}.${EditPuzzleField.Name}`}
                        control={control}
                        error={formErrors.puzzles?.[index]?.[EditPuzzleField.Name]}
                        width="320px"
                        defaultValue={puzzleDocumentData?.name}
                      />

                      <Input
                        required
                        id={`puzzles.${index}.keywords`}
                        label="Keywords"
                        placeholder="keywords"
                        name={`puzzles.${index}.keywords`}
                        control={control}
                        defaultValue={puzzleDocumentData?.keywords}
                        error={formErrors.puzzles?.[index]?.keywords}
                        width="320px"
                      />
                    </FlexContainer>

                    <FlexContainer gap={gapBetweenFields}>
                      <SelectForm
                        id={`puzzles.${index}.primaryCategory`}
                        label={'Primary category'}
                        name={`puzzles.${index}.${EditPuzzleField.Collection}`}
                        placeholder="Select Category"
                        control={control}
                        width="320px"
                        values={categorySelectOptions}
                        error={formErrors.puzzles?.[index]?.[EditPuzzleField.Collection]}
                        required
                      />

                      <Button
                        className="exchange-categories"
                        onClick={() => {
                          const tempValue =
                            getValues('puzzles')?.[index][EditPuzzleField.Collection];
                          const secondaryCollection =
                            getValues('puzzles')?.[index][EditPuzzleField.SecondaryCollection];

                          if (!secondaryCollection || !tempValue) return;

                          setValue(
                            `puzzles.${index}.${EditPuzzleField.Collection}`,
                            // @ts-expect-error For some reason type inference doesn't work correctly and expects "never" as value.
                            secondaryCollection,
                          );
                          setValue(
                            `puzzles.${index}.${EditPuzzleField.SecondaryCollection}`,
                            // @ts-expect-error For some reason type inference doesn't work correctly and expects "never" as value.
                            tempValue,
                          );
                        }}
                        style={{
                          width: '50px',
                          fontSize: '1.5rem',
                          borderRadius: '100%',
                          aspectRatio: 1,
                        }}
                      >
                        ⇆
                      </Button>

                      <SelectForm
                        required
                        id={`puzzles.${index}.secondaryCategory`}
                        label={'Secondary category'}
                        name={`puzzles.${index}.${EditPuzzleField.SecondaryCollection}`}
                        placeholder="Select Category"
                        control={control}
                        width="320px"
                        values={categorySelectOptions}
                        isClearable={true}
                      />
                    </FlexContainer>

                    <FlexContainer justifyContent="space-between">
                      <SelectForm
                        required
                        id={`puzzles.${index}.publisher`}
                        label="Publisher"
                        name={`puzzles.${index}.${EditPuzzleField.Publisher}`}
                        placeholder="artist"
                        control={control}
                        width="320px"
                        values={[]}
                        error={formErrors.puzzles?.[index]?.[EditPuzzleField.Publisher]}
                        disabled
                      />

                      <FlexRow>
                        <SelectForm
                          required
                          id={`puzzles.${index}.status`}
                          label="Status"
                          name={`puzzles.${index}.status`}
                          placeholder="Select status"
                          onChange={(option) => {
                            triggerFormValidation('artist.status');
                            option?.value === 'rejected' &&
                              openRejectionModal('puzzle', index, field.puzzleId);
                          }}
                          control={control}
                          width="320px"
                          values={STATUS_DROPDOWN_OPTIONS}
                          error={formErrors.puzzles?.[index]?.status}
                        />

                        {(puzzleDocumentData.status === PUZZLE_STATUSES.rejected ||
                          puzzleDocumentData.status === PUZZLE_STATUSES.edited) && (
                          <InformationModal
                            type={InformationModalTypes.Error}
                            title="Rejection reason"
                            content={puzzleDocumentData.rejectMessage}
                          />
                        )}
                      </FlexRow>
                    </FlexContainer>

                    <FlexContainer justifyContent="space-between" gap={gapBetweenFields}>
                      <FlexContainer justifyContent="space-between" gap={gapBetweenFields}>
                        <FlexContainer
                          flexDirection="row"
                          justifyContent="space-evenly"
                          alignItems="center"
                          gap="1rem"
                        >
                          <Input
                            id={`puzzles.${index}.aiGenerated`}
                            control={control}
                            type="checkbox"
                            width="25px"
                            minWidth="25px"
                            name={`puzzles.${index}.aiGenerated`}
                            placeholder={strings.aiGenerated}
                          />

                          <Text size={0.8}>{'AI Generated'}</Text>
                        </FlexContainer>
                      </FlexContainer>
                    </FlexContainer>
                  </FlexContainer>
                </FlexContainer>
              </Box>
            );
          })}

          <h2>Coloring Grids</h2>
          {gridFields.length === 0 && <Text>No coloring grids to review.</Text>}
          {gridFields.map((field, index) => {
            if (!field.gridId) return <></>;
            const gridDocumentData = gridsMap[field.gridId]?.data();

            const previewImageUrlLink =
              gridDocumentData?.type === 'video'
                ? `${STORAGE_URL}/${gridDocumentData?._meta?.original}`
                : gridDocumentData?.url;

            const previousVersionUrl = buildPreviousVersionUrl({
              type: gridDocumentData.type,
              url: gridDocumentData.url,
              uuid: field.gridId,
            });

            return (
              <Box key={field.id} width="100%" margin="32px 0" padding="32px">
                <FlexContainer gap="2rem">
                  <div>
                    <a href={previewImageUrlLink} target="_blank" rel="noreferrer">
                      <ColoringGridImage
                        width="256px"
                        height="256px"
                        cursor="pointer"
                        src={gridDocumentData?.iconUrl}
                        borderRadiusType="rounded"
                        objectFit="contain"
                        alt="Picture"
                      />
                    </a>
                    <FlexContainer justifyContent="center" margin="10px 0 0 0">
                      {gridDocumentData?.status === Status.Edited && (
                        <LinkButton
                          href={previousVersionUrl}
                          target="_blank"
                          rel="noopener noreferer"
                          style={{ marginTop: '8px' }}
                        >
                          Previous Version
                        </LinkButton>
                      )}
                    </FlexContainer>
                  </div>

                  <FlexContainer flexDirection="column" gap="2.5rem" justifyContent="space-between">
                    <FlexContainer
                      gap={gapBetweenFields}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Input
                        required
                        id={`coloringGrids.${index}.name`}
                        label="Name"
                        placeholder="Name"
                        name={`coloringGrids.${index}.name`}
                        control={control}
                        error={formErrors.coloringGrids?.[index]?.name}
                        width="320px"
                        defaultValue={gridDocumentData?.name}
                      />

                      <Input
                        required
                        id={`coloringGrids.${index}.keywords`}
                        label="Keywords"
                        placeholder="keywords"
                        name={`coloringGrids.${index}.keywords`}
                        control={control}
                        defaultValue={gridDocumentData?.keywords}
                        error={formErrors.coloringGrids?.[index]?.keywords}
                        width="320px"
                      />
                    </FlexContainer>

                    <FlexContainer gap={gapBetweenFields}>
                      <SelectMultiForm
                        id={`coloringGrids.${index}.collections`}
                        label={'Categories'}
                        name={`coloringGrids.${index}.collections`}
                        placeholder="Select Categories"
                        control={control}
                        width="320px"
                        values={gridCategorySelectOptions}
                        error={formErrors.coloringGrids?.[index]?.collections}
                        required
                      />
                    </FlexContainer>

                    <FlexContainer justifyContent="space-between">
                      <SelectForm
                        required
                        id={`coloringGrids.${index}.publisher`}
                        label="Publisher"
                        name={`coloringGrids.${index}.publisher`}
                        placeholder="artist"
                        control={control}
                        width="320px"
                        values={[]}
                        error={formErrors.coloringGrids?.[index]?.publisher}
                        disabled
                      />

                      <FlexRow>
                        <SelectForm
                          required
                          id={`coloringGrids.${index}.status`}
                          label="Status"
                          name={`coloringGrids.${index}.status`}
                          placeholder="Select status"
                          onChange={(option) => {
                            triggerFormValidation('artist.status');
                            option?.value === 'rejected' &&
                              openRejectionModal('coloringGrid', index, field.gridId);
                          }}
                          control={control}
                          width="320px"
                          values={STATUS_DROPDOWN_OPTIONS}
                          error={formErrors.coloringGrids?.[index]?.status}
                        />

                        {(gridDocumentData.status === PUZZLE_STATUSES.rejected ||
                          gridDocumentData.status === PUZZLE_STATUSES.edited) && (
                          <InformationModal
                            type={InformationModalTypes.Error}
                            title="Rejection reason"
                            content={gridDocumentData.rejectMessage}
                          />
                        )}
                      </FlexRow>
                    </FlexContainer>

                    <FlexContainer justifyContent="space-between" gap={gapBetweenFields}>
                      <FlexContainer justifyContent="space-between" gap={gapBetweenFields}>
                        <FlexContainer
                          flexDirection="row"
                          justifyContent="space-evenly"
                          alignItems="center"
                          gap="1rem"
                        >
                          <Input
                            id={`coloringGrids.${index}.aiGenerated`}
                            control={control}
                            type="checkbox"
                            width="25px"
                            minWidth="25px"
                            name={`coloringGrids.${index}.aiGenerated`}
                            placeholder={strings.aiGenerated}
                          />

                          <Text size={0.8}>{'AI Generated'}</Text>
                        </FlexContainer>
                      </FlexContainer>
                    </FlexContainer>
                  </FlexContainer>
                </FlexContainer>
              </Box>
            );
          })}

          {/* ARTIST FORM */}
          <Box margin="50px auto 50px auto">
            <FlexContainer flexDirection="column" margin="1rem 0 0 0">
              <FlexContainer
                flexWrap="wrap"
                justifyContent="space-around"
                gap="15px"
                padding="15px"
              >
                <FlexContainer
                  flexDirection="column"
                  alignItems="center"
                  width="250px"
                  style={{ position: 'relative' }}
                >
                  <Image
                    height="160px"
                    width="160px"
                    style={{
                      objectFit: 'cover',
                      objectPosition: 'center',
                    }}
                    borderRadiusType="circle"
                    src={artistSnapshot?.data()?.profileImageUrl ?? ''}
                  />
                </FlexContainer>
                <FlexContainer
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  flex="1"
                  style={{ position: 'relative' }}
                >
                  <Image
                    borderRadiusType="rounded"
                    src={artistSnapshot?.data()?.coverImageUrl ?? ''}
                    style={{
                      objectFit: 'cover',
                      objectPosition: 'center',
                    }}
                    width="90%"
                    height="190px"
                  />
                </FlexContainer>
              </FlexContainer>
              <FlexContainer flexDirection="column" gap="40px" margin="50px 0 0 0">
                <FlexContainer flexWrap="wrap" justifyContent="space-around">
                  <Input
                    id="artist-name"
                    placeholder={strings.createAccNamePlaceholder}
                    name={`artist.${UpdateField.Name}`}
                    type="text"
                    label="Full name"
                    control={control}
                    error={formErrors.artist?.[UpdateField.Name]}
                    placeholderIcon={User}
                    width="400px"
                    defaultValue={artistSnapshot?.data()?.Name}
                    required
                  />
                  <Input
                    id="artist-displayName"
                    placeholder={strings.displayName}
                    name={`artist.${UpdateField.DisplayName}`}
                    type="text"
                    label="Display name"
                    control={control}
                    error={formErrors.artist?.[UpdateField.DisplayName]}
                    width="400px"
                    marginBottom={20}
                    autoComplete={false}
                    defaultValue={artistSnapshot?.data()?.username}
                    required
                  />
                </FlexContainer>
                <FlexContainer flexWrap="wrap" justifyContent="space-around">
                  <SelectForm
                    id="artist-country"
                    placeholder={strings.selectCountry}
                    name={`artist.${UpdateField.Country}`}
                    label="Country"
                    control={control}
                    values={COUNTRY_SELECT_OPTIONS}
                    width="400px"
                    defaultValue={artistCountry ?? null}
                    required
                  />
                  <Input
                    required
                    label="Portofolio Link"
                    placeholder="Enter Portfolio Link"
                    name={`artist.${UpdateField.PortfolioLink}`}
                    type="text"
                    control={control}
                    error={formErrors.artist?.[UpdateField.PortfolioLink]}
                    width="400px"
                    marginBottom={20}
                    autoComplete={false}
                    defaultValue={artistSnapshot?.data()?.portfolioUrl}
                    {...(artistValues?.portfolioUrl
                      ? {
                          labelOnClick: () => window.open(artistValues.portfolioUrl, '_blank'),
                        }
                      : {})}
                  />
                </FlexContainer>
                <FlexContainer flexWrap="wrap" justifyContent="space-around">
                  <Input
                    id="artist-profileLink"
                    placeholder="Enter Profile Link"
                    name={`artist.${UpdateField.ProfileLink}`}
                    label="Profile link"
                    type="text"
                    control={control}
                    error={formErrors.artist?.[UpdateField.ProfileLink]}
                    width="400px"
                    marginBottom={20}
                    autoComplete={false}
                    defaultValue={artistSnapshot?.data()?.profileLink}
                    required
                  />
                  <Input
                    id="artist-revenueShare"
                    placeholder={strings.revenueShare}
                    name={`artist.${UpdateField.RevenueShare}`}
                    label="Revenue share"
                    type="number"
                    control={control}
                    error={formErrors.artist?.[UpdateField.RevenueShare]}
                    width="400px"
                    autoComplete={false}
                    interval={{
                      min: 0,
                      max: 100,
                    }}
                    defaultValue={undefined}
                    required
                  />
                </FlexContainer>
                <FlexContainer flexWrap="wrap" justifyContent="space-around">
                  <FlexContainer gap="40px" flexDirection="column">
                    <SelectForm
                      id="artist-status"
                      placeholder="Select artist status"
                      label="Status"
                      name={`artist.${UpdateField.Status}`}
                      control={control}
                      onChange={(option) => {
                        triggerFormValidation(`artist.revenueShare`);

                        puzzleFields.forEach((field, index) =>
                          triggerFormValidation(`puzzles.${index}.status`),
                        );

                        gridFields.forEach((field, index) =>
                          triggerFormValidation(`coloringGrids.${index}.status`),
                        );

                        if (option?.value === 'rejected') {
                          openRejectionModal('artist');
                        } else {
                          // @ts-expect-error There is some typescript inference issue that expects value "never" here which isn't correct. Same as lines above.
                          setValue('artist.rejectMessage', undefined);
                        }
                      }}
                      values={ARTIST_STATUS_DROPDOWN_ITEMS}
                      error={formErrors.artist?.[UpdateField.Status]}
                      width="400px"
                      defaultValue={artistStatus ?? null}
                      required
                    />
                  </FlexContainer>

                  <Input
                    id="artist-description"
                    placeholder={strings.description}
                    name={`artist.${UpdateField.Description}`}
                    label="Description"
                    type="textarea"
                    control={control}
                    error={formErrors.artist?.[UpdateField.Description]}
                    width="400px"
                    height="200px"
                    autoComplete={false}
                    defaultValue={artistSnapshot?.data()?.Description}
                    required
                  />
                </FlexContainer>

                <FlexContainer justifyContent="space-around"></FlexContainer>
              </FlexContainer>
            </FlexContainer>
          </Box>

          <Button
            width={400}
            height={58}
            type="submit"
            disabled={dataFetchState !== 'success' || isSubmittingData}
            style={{ display: 'block', margin: '0 auto' }}
          >
            {dataFetchState !== 'success' || isSubmittingData ? (
              <Splash color={theme.colors.white} width={25} height={25} />
            ) : (
              strings.submit
            )}
          </Button>

          {combinedErrors.length > 0 && (
            <Box margin="32px auto 0 auto">
              <p>
                Error occurred while trying to fetch/submit page data. <br />
                Error message that might be useful for devs if error persists:
              </p>

              <code
                style={{
                  display: 'block',
                  padding: '16px',
                  marginTop: '32px',
                  border: `1px solid ${theme.colors.red}`,
                }}
              >
                {combinedErrors.map((error, index) =>
                  typeof error === 'string' ? (
                    <pre key={index}>{JSON.stringify(JSON.parse(error), null, 2)}</pre>
                  ) : (
                    <pre key={index}>
                      {JSON.stringify(error, Object.getOwnPropertyNames(error), 2)}
                    </pre>
                  ),
                )}
              </code>
            </Box>
          )}
        </form>
      )}
    </Container>
  );
};

export default FirstApprovalScreen;
